import { useState, type ReactNode } from 'react';

import { SHORTCUTS } from '../keyboard.js';

/**
 * The overlays: the shortcut list, the onboarding question, and adding a feed.
 *
 * Small enough to live together, and they share one visual treatment — a
 * dialog over the reader — so keeping them in one file keeps that consistent.
 */

/** M1.17.1's fourth criterion: the shortcuts are documented in the app. */
export function ShortcutHelp({ onClose }: { onClose: () => void }): ReactNode {
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
      <div className="overlay__card">
        <h2 className="overlay__title">Keyboard shortcuts</h2>
        <dl className="shortcuts">
          {SHORTCUTS.map((shortcut) => (
            <div className="shortcuts__row" key={shortcut.keys}>
              <dt className="shortcuts__keys">{shortcut.keys}</dt>
              <dd className="shortcuts__description">{shortcut.description}</dd>
            </div>
          ))}
        </dl>
        <button className="button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * The single onboarding question (M1.18.1, [D18](../../../Design-Decisions.md)).
 *
 * **One question, asked once, not a settings page.** D18's risk runs both
 * ways: a precise red badge on first launch contradicts the positioning within
 * five seconds, and an Inbox mode nobody can find makes Maya conclude the
 * product cannot count. Asking plainly is what serves both — and the answer is
 * recorded, because it is the only evidence H8 will ever get.
 *
 * Quiet is pre-selected and the dialog can be dismissed, which means the
 * default holds for anybody who does not care.
 */
export function InboxModeQuestion({
  onChoose,
}: {
  onChoose: (inboxMode: boolean) => void;
}): ReactNode {
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Unread counts">
      <div className="overlay__card">
        <h2 className="overlay__title">Do you want unread counts?</h2>
        <p className="overlay__text">
          By default this reader is quiet: no numbers, no badges. Nothing is waiting for
          you.
        </p>
        <p className="overlay__text">
          If you would rather see exactly how much is unread — per feed, per folder and
          in total — Inbox mode turns the numbers on. You can change this later.
        </p>
        <div className="overlay__actions">
          <button
            className="button button--primary"
            onClick={() => {
              onChoose(false);
            }}
          >
            Keep it quiet
          </button>
          <button
            className="button"
            onClick={() => {
              onChoose(true);
            }}
          >
            Show me the counts
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Adding a feed.
 *
 * Takes whatever somebody pastes. The discovery in M1.8.1 finds the feed from
 * a blog's home page, so the placeholder says so — asking a person for a
 * "feed URL" is asking them to know something they should not have to.
 */
export function AddFeed({
  onSubmit,
  onClose,
  busy,
  error,
}: {
  onSubmit: (url: string) => void;
  onClose: () => void;
  busy: boolean;
  error: string | undefined;
}): ReactNode {
  const [url, setUrl] = useState('');

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Add a feed">
      <div className="overlay__card">
        <h2 className="overlay__title">Add a feed</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(url.trim());
          }}
        >
          <label className="field">
            <span className="field__label">Address</span>
            <input
              className="field__input"
              type="text"
              value={url}
              placeholder="example.com — or the feed URL, if you have it"
              autoFocus
              onChange={(event) => {
                setUrl(event.target.value);
              }}
            />
          </label>

          {error !== undefined && (
            <p className="auth__error" role="alert">
              {error}
            </p>
          )}

          <div className="overlay__actions">
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? 'Looking…' : 'Add'}
            </button>
            <button className="button" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
